import { EditableFieldProps } from '../core-field/editable-field-json-props'
import {
  FIRE_DEFAULT_DEBOUNCE_MS,
  FireButtonVariant,
  FireFieldProps,
  FireInlineLookupProps,
  FireMode,
  ResolvableValue,
} from '../core-field/fire-field-props'

/**
 * Wider onChange signature used by the API field. The renderer's entity-bind
 * hook passes optional `silent` (skip pipeline broadcast) and `overridePath`
 * (write to a sibling path) flags on some paths; the API field forwards them
 * verbatim so callers can bind through those channels.
 */
export type UI2ApiFieldOnChange<T> = (
  value: T | undefined,
  silentUpdate?: boolean,
  overridePath?: string,
) => void

// Re-exported under the field's historical names so existing imports keep working.
export type ApiFireMode = FireMode
export type ApiButtonVariant = FireButtonVariant

/** An apiDefinition returns an HTTP status, so failure can be classified by transport vs status. */
export type ApiRetryType = 'never' | 'onNetworkError' | 'onFailure'

export interface ApiFieldInputs {
  headers?: Record<string, ResolvableValue<string>>
  queryParams?: Record<string, ResolvableValue<string>>
  pathParams?: Record<string, ResolvableValue<string>>
  payload?: ResolvableValue<Record<string, unknown>>
}

export interface ApiFieldResponse {
  success: boolean
  statusCode: number
  responseBody: unknown | null
  errorMsg: string | null
}

export const UI2_API_FIELD_DEFAULT_PROPS: Partial<UI2ApiFieldProps> = {
  fireMode: 'onClick',
  buttonLabel: 'Submit',
  retryType: 'never',
  debounceMs: FIRE_DEFAULT_DEBOUNCE_MS,
}

export interface UI2ApiFieldProps
  extends Omit<EditableFieldProps<ApiFieldResponse>, 'onChange'>,
    FireFieldProps,
    FireInlineLookupProps {
  onChange?: UI2ApiFieldOnChange<ApiFieldResponse>
  apiDefinitionReferenceKey: string
  /**
   * Expressions resolving the request's headers, params and payload. These are materialized by the
   * renderer against the frames before the field is mounted, so they can reference anything on the
   * form — but nothing that exists only inside the component.
   */
  inputs?: ApiFieldInputs

  /**
   * Same shape as `inputs`, but the `lazy` prefix keeps the renderer from materializing it, so the
   * field resolves the expressions itself with its own state in scope — notably `lookupQuery`, the
   * query submitted under `fireMode: 'inlineLookup'`. Expressions are resolved at any depth.
   *
   * Deep-merged over `inputs`, so one field can mix form-driven and lookup-driven values.
   */
  lazyInputs?: ApiFieldInputs
  retryType?: ApiRetryType
  /** Status codes that qualify for a retry. Only meaningful with `retryType: 'onFailure'`. */
  retryableStatusCodes?: number[]
}
